import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import type { MessageActionContentRenderer } from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { parseJsonValue } from '@atlassian/conversation-assistant-utils/parse-json-value';

import { ActionCardWrapper } from '../../common/ui/action-card-wrapper';

import {
	getReadonlyActionProduct,
	getReadonlyActionRenderer,
	getReadonlyActionTitle,
} from './use-action-renderer';

const messages = defineMessages({
	fallbackTitle: {
		id: 'conversation-assistant-message-actions.controllers.message-actions.ReadOnlyDefaultActionCards.fallbackTitle',
		defaultMessage: 'Action unavailable',
		description:
			'Fallback title for a read-only action card when no local action title is registered.',
	},
	unavailableMessage: {
		id: 'conversation-assistant-message-actions.controllers.message-actions.ReadOnlyDefaultActionCards.unavailableMessage',
		defaultMessage: 'This action is not available in this read-only view.',
		description:
			'Body text shown in a read-only chat response where an interactive action originally appeared.',
	},
});

const styles = cssMap({
	card: {
		maxWidth: '500px',
	},
});

export type ReadOnlyDefaultActionCardsProps = {
	actions: ReadonlyArray<MessageAction>;
	ContentRenderer?: MessageActionContentRenderer;
	productKey?: string;
	cloudId?: string;
	conversationId?: string;
	messageId?: string;
};

export const ReadOnlyDefaultActionCards = ({
	actions,
	ContentRenderer,
	productKey,
	cloudId,
	conversationId,
	messageId,
}: ReadOnlyDefaultActionCardsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!actions.length) {
		return null;
	}

	return (
		<Stack space="space.200">
			{actions.map((action) => {
				const normalizedAction = {
					...action,
					data: parseJsonValue(action.data),
					executedData: parseJsonValue(action.executedData),
				};
				const readonlyTitle = getReadonlyActionTitle(normalizedAction.key);
				const ReadOnlyRenderer = getReadonlyActionRenderer(normalizedAction);
				const defaultCard = (
					<Box key={normalizedAction.invocationId} xcss={styles.card}>
						<ActionCardWrapper
							title={formatMessage(readonlyTitle ?? messages.fallbackTitle)}
							product={getReadonlyActionProduct(normalizedAction.key)}
						>
							<Text>{formatMessage(messages.unavailableMessage)}</Text>
						</ActionCardWrapper>
					</Box>
				);

				if (ReadOnlyRenderer && ContentRenderer && productKey) {
					return (
						<Suspense
							key={normalizedAction.invocationId}
							fallback={defaultCard}
							interactionName="readonly-action-card-renderer"
						>
							<ReadOnlyRenderer
								actions={[normalizedAction]}
								ContentRenderer={ContentRenderer}
								productKey={productKey}
								cloudId={cloudId ?? ''}
								conversationId={conversationId}
								messageId={messageId ?? normalizedAction.invocationId}
							/>
						</Suspense>
					);
				}

				return defaultCard;
			})}
		</Stack>
	);
};
